import { useState, useCallback } from 'react';
import { Upload, Loader2, AlertCircle, FileUp } from 'lucide-react';
import { Progress } from "@/components/UI/progress";
import { Alert, AlertDescription } from "@/components/UI/alert";
import { loamsApi } from '@/services/api';
import { useToast } from "@/components/UI/use-toast";

// Import Excel icon
import xlsxIcon from '@/assets/xlsx.png';

export const FileUpload = ({ onUploadComplete }) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const validateExcelFile = useCallback((file) => {
    console.log('[LOAMS] Validating file:', {
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024 / 1024).toFixed(2)} MB`
    });

    if (!file) {
      throw new Error('No file selected');
    }

    const fileExtension = file.name.split('.').pop().toLowerCase();
    const validExtensions = ['xlsx', 'xls'];
    
    const validMimeTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/octet-stream',
      'application/x-excel',
      'application/x-msexcel',
      ''
    ];

    if (!validExtensions.includes(fileExtension)) {
      throw new Error(`Invalid file extension: .${fileExtension}. Only .xlsx and .xls files are allowed.`);
    }

    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      throw new Error(`File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds 50MB limit.`);
    }

    if (file.size > 10 * 1024 * 1024) { // Over 10MB
      console.warn('[LOAMS] Large file detected, processing may take longer');
    }

    if (file.size === 0) {
      throw new Error('File appears to be empty');
    }

    return true;
  }, []);

  const handleFile = async (file) => {
    try {
      setError(null);
      setUploading(true);
      setProgress(0);

      validateExcelFile(file);

      const formData = new FormData();
      formData.append('file', file);

      // Add upload timeout handling
      const timeoutId = setTimeout(() => {
        if (!uploading) return;
        setError('Upload is taking longer than expected. Please wait...');
      }, 30000);

      // Using the new previewAndSave method with saveToDb = false for initial preview
      const response = await loamsApi.previewAndSave(formData, false);
      clearTimeout(timeoutId);
      
      setProgress(100);

      if (response.data.totalRows > 100) {
        toast({
          title: "Large File Detected",
          description: "Processing may take longer than usual due to the number of rows",
          variant: "warning",
          duration: 6000
        });
      }

      if (onUploadComplete) {
        onUploadComplete(file, response.data);
      }

      toast({
        title: "File Uploaded Successfully",
        description: `Found ${response.data.totalRows || 0} rows to process`,
        variant: "success"
      });

    } catch (err) {
      setProgress(0);
      const errorMessage = err.response?.data?.details || 
                          err.response?.data?.error ||
                          err.message ||
                          'An unexpected error occurred';
      
      console.error('[LOAMS] Upload error:', err);
      
      setError(errorMessage);
      toast({
          variant: "destructive",
          title: "Upload Failed",
          description: errorMessage,
          duration: 5000
      });
  } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = async (event) => {
    if (event.target.files && event.target.files[0]) {
      await handleFile(event.target.files[0]);
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-12 transition-colors duration-200 ease-in-out 
          ${dragActive ? 'border-amber-500 bg-amber-50' : 'border-gray-300 hover:border-amber-300'}`}
      >
        <div className="flex flex-col items-center">
          <div className="mb-4">
            {uploading ? (
              <Loader2 className="h-12 w-12 text-amber-500 animate-spin" />
            ) : (
              <div className="h-14 w-14">
                <img 
                  src={xlsxIcon} 
                  alt="Excel file" 
                  className="h-full w-full object-contain"
                />
              </div>
            )}
          </div>
          
          <h3 className="text-lg font-medium text-gray-700 mt-2">
            {uploading ? 'Processing Excel file...' : 'Upload Excel file'}
          </h3>
          
          <div className="mt-4 flex text-sm leading-6 text-gray-600">
            <label
              htmlFor="file-upload"
              className="relative cursor-pointer rounded-md font-semibold text-amber-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-amber-600 focus-within:ring-offset-2 hover:text-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>{uploading ? 'Processing...' : 'Choose a file'}</span>
              <input
                id="file-upload"
                name="file-upload"
                type="file"
                className="sr-only"
                onChange={handleFileChange}
                accept=".xlsx,.xls"
                disabled={uploading}
                aria-label="Upload Excel file"
              />
            </label>
            <p className="pl-1">or drag and drop</p>
          </div>
          
          <p className="text-xs leading-5 text-gray-600 mt-2">
            Excel files (.xlsx, .xls) up to 50MB
          </p>

          {(uploading || progress > 0) && (
            <div className="w-full mt-6 max-w-xs">
              <Progress 
                value={progress} 
                className="w-full h-2 bg-gray-200"
                variant={progress === 100 ? "success" : "default"}
              />
              <p className="text-xs text-center mt-2 text-gray-500">
                {progress < 100 ? 'Processing...' : 'Complete'} {progress}%
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;